import React, { useEffect, useState } from 'react';
import { Card, Col, Icon, message, Row, Skeleton } from 'antd';
import styled from 'styled-components/macro';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

import { getCampaignHostProfileDetails } from 'utils/apis/campaigns';
import { numberWithCommas } from 'utils/helpers/helpers';
import { useGetVoteDetails } from 'utils/hooks/campaign';
import { useFbScript, useFbUser } from 'utils/hooks/fb';
import { useFetchConstant } from 'utils/hooks/general';
import { buildAwardsRoute, buildHostProfileListingRoute } from 'utils/routes';

import CampaignCard from 'components/CampaignCard/CampaignCard';
import CampaignSignUpModal from 'components/CampaignSignUpModal/CampaignSignUpModal';
import Description from 'components/Description/Description';
import Heading from 'components/Heading/Heading';
import ImageGallery from 'components/ImageGallery/ImageGallery';

import DetailsSection from './components/DetailsSection/DetailsSection';

const RootContainer = styled(Card)`
  &&& {
    margin: 0 auto;
    border: 0;
    width: 100%;

    @media (min-width: 768px) {
      width: 80%;
    }
  }
`;

const SectionDivider = styled.div`
  border: 0.5px solid ${props => props.theme.color.borderColor};
  margin: 32px 0;
`;

const ProfilePhotoContainer = styled(Col)`
  display: flex;
  justify-content: center;
  border: 1px solid #e8e8e8;
  max-height: 560px;
  overflow: hidden;
`;

const ProfilePhoto = styled.img`
 width: auto;
 height: auto;
 margin: auto;
 max-width: 100%;
 max-height: 100%;
`

const BaseIcon = styled(Icon)`
  font-size: 18px;
  margin-right: 4px;
`;

const VoteIcon = styled(BaseIcon)`
  color: #565656;
`;

const VoteButton = styled.div`
  display: flex;
  padding: 8px 32px;
  border: 1px solid ${props => props.theme.color.secondary};
  border-radius: 4px;
  background-color: ${props => props.theme.color.secondary};
  cursor: pointer;
`;

const ActionButton = styled.div`
  display: flex;
  border: 2px solid #ffc20e;
  padding: 8px 20px;
  border-radius: 4px;

  :hover {
    ${props => !props.noaction && `cursor: pointer; color: ${props.theme.color.secondary}`};
  }
`;

const ActionIcon = styled(BaseIcon)`
  color: #ffc20e;
`;

const useGetHostProfileDetails = id => {
  const { selections: states, isLoading } = useFetchConstant('statesMY');
  const [hostDetails, setHostDetails] = useState({});
  const [isHostDetailsLoading, setIsHostDetailsLoading] = useState(true);
  useEffect(() => {
    if (!isLoading) {
      getCampaignHostProfileDetails(id).then(data => {
        const formattedCampaignsHosts = formatHostDetailsCoveredAreas(data, states);
        setHostDetails(formattedCampaignsHosts);
        setIsHostDetailsLoading(false);
      });
    }
  }, [id, isLoading, states]);
  return { hostDetails, isHostDetailsLoading };
};

const formatHostDetailsCoveredAreas = (hostDetails = {}, states = []) => {
  if (hostDetails.coveredAreas && hostDetails.coveredAreas.length > 0) {
    hostDetails.coveredAreas = hostDetails.coveredAreas.map(coveredArea => {
      const foundState = states.find(state => String(state.code) === String(coveredArea));
      return foundState && foundState.label;
    });
  }
  return hostDetails;
};

const getVoteCountForDisplay = (isJustVoted, voteCount) => {
  const finalVoteCount = isJustVoted ? voteCount + 1 : voteCount;
  const voteText = finalVoteCount > 1 ? 'votes' : 'vote';
  return `${numberWithCommas(finalVoteCount, 0)} ${voteText}`;
};

const getErrorPromptMsg = type => `You've reached your vote limit for this ${type}. Come back and vote again tomorrow.`;

const HostProfile = ({
  match: {
    params: { id }
  },
  location: { pathname }
}) => {
  const isDisplayForHostAwards = pathname.startsWith(buildAwardsRoute());
  const { originalSelection: campaignVotingTypesConstant } = useFetchConstant('campaignVotingTypes');
  const { hostDetails, isHostDetailsLoading } = useGetHostProfileDetails(id);

  const [isJustVoted, setIsJustVoted] = useState(false);
  const voteCountText = getVoteCountForDisplay(isJustVoted, hostDetails.voteCount);

  const { user, loginFb } = useFbUser();
  const { shareToFb } = useFbScript();
  const { votedHosts, votedUnits, saveAndSubmitVote } = useGetVoteDetails(user.id);
  const [selectedUnitPhotos, setSelectedUnitPhotos] = useState([]);

  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [tempSelectedType, setTempSelectedType] = useState('');
  const [tempSelectedCurrentItemId, setTempSelectedCurrentItemId] = useState('');

  useEffect(() => {
    if (tempSelectedType && tempSelectedCurrentItemId && user.isConnected) {
      saveAndSubmitVote(tempSelectedType, tempSelectedCurrentItemId, user);
      setTempSelectedType('');
      setTempSelectedCurrentItemId('');
    }
  }, [tempSelectedType, tempSelectedCurrentItemId, user, saveAndSubmitVote]);

  const handleOnVote = async (type, currentItemId) => {
    if (user.isConnected) {
      saveAndSubmitVote(type, currentItemId, user);
    } else {
      setIsSignUpModalOpen(true);
      setTempSelectedType(type);
      setTempSelectedCurrentItemId(currentItemId);
    }
  };

  const handleOnSignUpModalOk = async () => {
    setIsSignUpModalOpen(false);
    await loginFb();
  };

  const handleOnSignUpModalClose = () => {
    setIsSignUpModalOpen(false);
  };

  // NOTE: document.execCommand only support input copy
  const handleOnCopyLinkClick = () => {
    const link = window.location.href;
    const tempInput = document.createElement('input');
    document.body.appendChild(tempInput);
    tempInput.value = link;
    tempInput.select();
    document.execCommand('copy');
    document.body.removeChild(tempInput);
    message.success("You've copied the link to this host profile page.");
  };

  const handleOnShareToFacebookClick = () => {
    shareToFb(window.location.href);
  };

  return (
    <RootContainer bodyStyle={{ paddingTop: '48px' }}>
      <Helmet>
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${hostDetails.name} | HostAStay`} />
        <meta property="og:description" content={hostDetails.briefDescription} />
        {hostDetails.logo && <meta property="og:image" content={hostDetails.logo} />}
        <title>{`${hostDetails.name} | HostAStay`}</title>
        <meta name="description" content={hostDetails.briefDescription} />
      </Helmet>
      {!isHostDetailsLoading ? (
        <>
          {isDisplayForHostAwards && (
            <CampaignSignUpModal visible={isSignUpModalOpen} onOk={handleOnSignUpModalOk} onModalClose={handleOnSignUpModalClose} />
          )}
          <Row type="flex" style={{ marginBottom: '16px' }}>
            <Col>
              <Link to={isDisplayForHostAwards ? buildAwardsRoute() : buildHostProfileListingRoute()}>
                <Icon type="left" style={{ marginRight: '8px' }} />
                View All Hosts
              </Link>
            </Col>
          </Row>
          <Row type="flex" justify="space-between">
            <ProfilePhotoContainer span={24} lg={8}>
              <ProfilePhoto src={hostDetails.logo} alt="host logo" />
            </ProfilePhotoContainer>
            <Col span={24} lg={15}>
              <Heading size="sub">{hostDetails.name}</Heading>
              {isDisplayForHostAwards && (
                <Row type="flex" align="middle" style={{ marginBottom: '16px' }}>
                  <ActionIcon type="heart" theme="filled" />
                  <span>{voteCountText}</span>
                </Row>
              )}
              <Description color="lightGrey">{hostDetails.briefDescription}</Description>
              <Row type="flex" gutter={16}>
                {isDisplayForHostAwards && (
                  <Col style={{ marginBottom: '4px' }}>
                    <VoteButton
                      onClick={() => {
                        if (!votedHosts.includes(id)) {
                          handleOnVote(campaignVotingTypesConstant.HOST.code, id);
                          setIsJustVoted(true);
                        } else {
                          message.warning(getErrorPromptMsg(campaignVotingTypesConstant.HOST.code));
                        }
                      }}
                    >
                      <VoteIcon type="heart" theme={!!votedHosts.includes(id) ? 'filled' : 'outlined'} />
                      <span>Vote</span>
                    </VoteButton>
                  </Col>
                )}
                <Col style={{ marginBottom: '4px' }}>
                  <ActionButton onClick={handleOnCopyLinkClick}>
                    <ActionIcon type="link" />
                    <span>Copy link</span>
                  </ActionButton>
                </Col>
                <Col style={{ marginBottom: '4px' }}>
                  <ActionButton onClick={handleOnShareToFacebookClick}>
                    <ActionIcon type="facebook" />
                    <span>Share to Facebook</span>
                  </ActionButton>
                </Col>
              </Row>
            </Col>
          </Row>
          <SectionDivider />
          <DetailsSection hostDetails={hostDetails} />
          {isDisplayForHostAwards && hostDetails.units && hostDetails.units.length > 0 && (
            <>
              <SectionDivider />
              <Row gutter={24}>
                <Heading size="sub">Instagrammable Units</Heading>
                {hostDetails.units.map(unit => (
                  <Col span={24} md={12} lg={8} style={{ marginBottom: '24px' }} key={unit._id}>
                    <CampaignCard
                      type={campaignVotingTypesConstant && campaignVotingTypesConstant.UNIT && campaignVotingTypesConstant.UNIT.code}
                      data={unit}
                      isVoted={!!votedUnits.includes(unit._id)}
                      onVote={() => handleOnVote(campaignVotingTypesConstant.UNIT.code, unit._id)}
                      onClickUnitPhoto={setSelectedUnitPhotos}
                    />
                  </Col>
                ))}
              </Row>
            </>
          )}
          <ImageGallery photos={selectedUnitPhotos} visible={selectedUnitPhotos.length > 0} onCloseImageGallery={() => setSelectedUnitPhotos([])} />
        </>
      ) : (
        <Skeleton />
      )}
    </RootContainer>
  );
};

export default HostProfile;
